import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { AuthInterceptorStateService } from './auth-interceptor-state.service';
import { EMPTY, catchError, of, throwError } from 'rxjs';
import { filter, finalize, switchMap, take } from 'rxjs/operators';
import { ApiEndpoint } from '../http/api';

/** URLs that must never trigger the 401 → refresh → retry cycle */
const AUTH_URLS = [ApiEndpoint.RefreshToken, ApiEndpoint.Authenticate, ApiEndpoint.Logout];

/**
 * Clone request with the latest CSRF token from document.cookie.
 *
 * After a token refresh the backend issues a NEW CSRF cookie via Set-Cookie.
 * The original request still carries the OLD X-XSRF-TOKEN header (set by
 * csrfInterceptor on the first pass). Since the retry goes through `next()`
 * — which only reaches inner interceptors — csrfInterceptor never runs again.
 * Without this, the retried request sends the stale CSRF header, the backend
 * compares it against the fresh cookie, and returns 403.
 */
function cloneWithFreshCsrf(req: HttpRequest<unknown>): HttpRequest<unknown> {
  const token = document.cookie.match(/(^| )XSRF-TOKEN=([^;]+)/)?.[2];
  return req.clone(
    token ? { headers: req.headers.set('X-XSRF-TOKEN', decodeURIComponent(token)) } : {},
  );
}

/**
 * Helper: trigger the shared terminal teardown, wait for navigation to complete, then reset
 * flags. Returns an Observable that completes after logout navigation finishes.
 *
 * Routes through authService.terminalLogout() — the SAME contract the proactive pre-expiry
 * caller uses — so the interceptor's terminal-401 path and the proactive path share ONE
 * teardown. When the reactive refresh already ran the terminal teardown (its _doHttpRefresh
 * classified the 401 as terminal), the _terminated latch makes this a no-op except the
 * navigation, so POST /logout still fires exactly once (no double-fire).
 *
 * refreshDone$.next(false) is emitted FIRST (unchanged) so every parked 401 request queued
 * on refreshDone$ is rejected before the teardown begins — the queue-drain ordering the
 * interceptor relies on is preserved.
 */
function performLogout(authService: AuthService, state: AuthInterceptorStateService) {
  // latchLoggingOut() đóng cổng VÀ hẹn giờ mở lại ngay tại đây. Đừng dời hẹn giờ xuống
  // switchMap/catchError bên dưới: chính cú điều hướng sang /login của terminalLogout()
  // huỷ LayoutComponent, kéo theo takeUntil(_unsubscribeAll) của các stream nền — mà
  // request 401 mở đầu thường đến từ đúng những stream ấy. Chuỗi bị unsubscribe thì hai
  // nhánh dưới KHÔNG BAO GIỜ chạy, và cổng đóng vĩnh viễn (xem doc-comment của
  // AuthInterceptorStateService).
  state.latchLoggingOut();
  state.refreshDone$.next(false);

  return authService.terminalLogout().pipe(
    switchMap(() => {
      // Chuỗi kết thúc bình thường — gia hạn cửa sổ từ lúc điều hướng đã xong.
      state.scheduleRelease();
      return EMPTY;
    }),
    catchError(() => {
      state.scheduleRelease();
      return EMPTY;
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip auth-related endpoints to prevent retry loops
  if (AUTH_URLS.some((url) => req.url.includes(url))) {
    return next(req);
  }

  const state = inject(AuthInterceptorStateService);

  // After a failed refresh triggers logout, silently drop all further API calls
  // to prevent 403 cascade from CSRF middleware seeing stale httpOnly cookies
  if (state.isLoggingOut) {
    return EMPTY;
  }

  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((err) => {
      if (err.status !== 401) {
        return throwError(() => err);
      }

      // If logout is already in progress, don't retry
      if (state.isLoggingOut) {
        return EMPTY;
      }

      // If a refresh is already in progress, queue this request
      if (state.isRefreshing) {
        return state.refreshDone$.pipe(
          filter((success) => success !== null),
          take(1),
          switchMap((success) => {
            if (success) {
              return next(cloneWithFreshCsrf(req));
            }
            // Refresh could not renew the token (terminal OR transient). Release this
            // parked request by propagating its OWN original 401 instead of leaving it
            // hanging forever. On the terminal path the tab is navigating to /login anyway;
            // on the transient path the caller sees the genuine failure while the session
            // stays alive (no teardown ran).
            return throwError(() => err);
          }),
        );
      }

      // First 401 — trigger refresh
      state.markRefreshing(true);

      // Đã bàn giao kết quả cho hàng đợi chưa? switchMap dưới đây là nơi bàn giao; nếu
      // chuỗi chết TRƯỚC đó (caller unsubscribe) thì finalize phải tự dọn, không thì
      // isRefreshing kẹt true và mọi 401 sau đó đỗ trên refreshDone$ vĩnh viễn.
      let settled = false;

      return authService.refreshAccessToken().pipe(
        // Safety net — refreshAccessToken() normally catches its own errors and returns a
        // boolean, but if it ever throws unexpectedly, fold that into `false` so the gate
        // below still runs (drains the parked queue and won't wrongly tear the tab down for
        // an unclassifiable error). The teardown decision is made ONLY by the terminal gate.
        catchError(() => of(false)),
        switchMap((success) => {
          settled = true;
          state.markRefreshing(false);

          if (success) {
            state.refreshDone$.next(true);
            return next(cloneWithFreshCsrf(req));
          }

          // refreshAccessToken() returns false for BOTH terminal and transient failures.
          // Gate on authService.terminated — the SAME gate the proactive caller uses
          // (_triggerProactiveRefresh: `!success && this._terminated`):
          //   • TERMINAL (terminated === true): the backend revoked the token family (or
          //     401'd). _doHttpRefresh already wiped credentials → run the shared teardown
          //     (revoke-once + navigate to /login, cross-tab broadcast) via performLogout.
          //     performLogout emits refreshDone$.next(false) FIRST, draining the parked queue.
          //   • TRANSIENT (terminated === false): a network blip / 5xx / timeout (any 400
          //     from the refresh endpoint is TERMINAL — fail-closed, review 2026-07-27).
          //     _doHttpRefresh PRESERVED credentials on purpose ("step 0"); the user is still
          //     logged in. Do NOT tear down or navigate. Drain the parked queue and propagate
          //     THIS request's own 401 — a later 401 will retry the refresh (capped at
          //     MAX_TRANSIENT_REFRESH_FAILURES consecutive misses inside AuthService).
          if (authService.terminated) {
            return performLogout(authService, state);
          }

          state.refreshDone$.next(false);
          return throwError(() => err);
        }),
        finalize(() => {
          if (settled) {
            return;
          }
          state.markRefreshing(false);
          state.refreshDone$.next(false);
        }),
      );
    }),
  );
};
